// Pure email-safe HTML rendering for newsletters. Inlined styles,
// table-based layout, MSO-compatible. The output is what gets
// snapshot-stored in `newsletter_sends.html_snapshot`; the per-
// recipient personalization pass (lib/personalize.ts) substitutes
// `{{first_name}}`, `{{unsubscribe_url}}`, etc. into this output
// before it goes to Resend.
//
// Lives in /shared so it's importable from both the worker (for
// the send pipeline) and the client (for the editor's export
// modal preview). No DOM, no React.

import type { Block, BlockType, Draft, EventListItem } from './types';
import { inlineMdToHtml } from './inline-md';
import { formatEventDate, todayIso, type EventLike } from './event-date';
import { storyBodyItems, resolveCalendarEvents } from './block-utils';

// Per-render context. Today this only carries the 8-day window of
// upcoming DB events used by the calendar block in auto-pull mode.
export interface RenderContext {
  dbEvents?: EventListItem[];
}

export const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!),
  );

// `rich` is for fields that support inline markdown (bold/italic/underline/
// links). Calling `esc` on those would double-escape; the parser already
// escapes whatever isn't a known mark.
const rich = (s: unknown): string => inlineMdToHtml(String(s ?? ''));

type Renderer = (data: Record<string, unknown>, ctx: RenderContext) => string;

interface ForecastDay {
  day: string;
  high: string;
  low: string;
  icon: string;
}
interface BulletinItem {
  headline: string;
  body: string;
  sourceLabel: string;
  sourceUrl: string;
}
interface PollOption {
  label: string;
  url: string;
}
interface Social {
  label: string;
  url: string;
}

const HTML_RENDERERS: Record<BlockType, Renderer> = {
  header: (d) => `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="force-light" style="background-color:#FFFFFF; border-radius:8px; overflow:hidden;">
                <tr>
                  <td align="center" class="px-mobile" style="padding: 28px 36px 20px 36px; background-color:#E2F4FF;">
                    <a href="https://penobscotmorning.com" style="text-decoration:none;">
                      <img src="${esc(d.logoUrl)}" alt="Penobscot Morning" width="240" height="48" class="logo-mobile" style="display:block; border:0; outline:none; max-width:240px; height:auto;">
                    </a>
                  </td>
                </tr>
                <tr>
                  <td class="px-mobile" style="padding: 16px 36px;">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                      <tr>
                        <td class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; font-size:13px; color:#5C6370; letter-spacing:0.4px; text-transform:uppercase; font-weight:600;">${esc(d.dateLine)}</td>
                        <td class="body" align="right" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; font-size:13px; color:#5C6370; letter-spacing:0.4px; text-transform:uppercase; font-weight:600;">${d.editionMode === 'custom' ? esc(d.editionNumber) : `Edition Nº ${esc(d.editionNumber)}`}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`,

  greeting: (d) => `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="force-light" style="background-color:#FFFFFF; border-radius:8px;">
                <tr>
                  <td class="px-mobile py-mobile" style="padding: 32px 36px;">
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 14px 0; font-size:17px; line-height:1.55; color:#1A1F2E;">
                      <strong style="color:#153EA7;">${esc(d.lead)}</strong> ${rich(d.body)}
                    </p>
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0; font-size:15px; line-height:1.5; color:#5C6370; font-style:italic;">— ${esc(d.signature)}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`,

  weather: (d) => `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="force-light" style="background-color:#FFFFFF; border-radius:8px;">
                <tr>
                  <td class="px-mobile" style="padding: 22px 36px 8px 36px;">
                    <span class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; font-size:11px; font-weight:700; letter-spacing:1.4px; color:#153EA7; text-transform:uppercase;">Penobscot at a Glance</span>
                  </td>
                </tr>
                <tr>
                  <td class="px-mobile" style="padding: 14px 36px 28px 36px;">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F5FAFE; border:1px solid #D6E7F4; border-radius:6px;">
                      <tr>
                        <td class="weather-stack" valign="top" width="33%" style="padding: 18px 16px; border-right: 1px solid #D6E7F4;">
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 6px 0; font-size:11px; font-weight:700; color:#5C6370; letter-spacing:0.8px; text-transform:uppercase;">Today</p>
                          <p class="display" style="font-family:'Fraunces',Georgia,serif; margin:0 0 6px 0; font-size:36px; font-weight:600; color:#153EA7; line-height:1; letter-spacing:-1px;">${esc(d.todayTemp)}</p>
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 4px 0; font-size:13px; color:#1A1F2E; font-weight:600;">${esc(d.todayIcon)} ${esc(d.todayLabel)}</p>
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0; font-size:12px; color:#5C6370;">High ${esc(d.todayHigh)} &middot; Low ${esc(d.todayLow)}</p>
                        </td>
                        <td class="weather-stack" valign="top" width="33%" style="padding: 18px 16px; border-right: 1px solid #D6E7F4;">
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 6px 0; font-size:11px; font-weight:700; color:#5C6370; letter-spacing:0.8px; text-transform:uppercase;">Tomorrow</p>
                          <p class="display" style="font-family:'Fraunces',Georgia,serif; margin:0 0 6px 0; font-size:30px; font-weight:600; color:#153EA7; line-height:1; letter-spacing:-1px;">${esc(d.tomorrowTemp)}</p>
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 4px 0; font-size:13px; color:#1A1F2E; font-weight:600;">${esc(d.tomorrowIcon)} ${esc(d.tomorrowLabel)}</p>
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0; font-size:12px; color:#5C6370;">High ${esc(d.tomorrowHigh)} &middot; Low ${esc(d.tomorrowLow)}</p>
                        </td>
                        <td class="weather-stack-last" valign="top" width="34%" style="padding: 18px 16px;">
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 8px 0; font-size:11px; font-weight:700; color:#5C6370; letter-spacing:0.8px; text-transform:uppercase;">Rest of Week</p>
                          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                            ${((d.forecast as ForecastDay[]) || []).map((f) => `<tr><td class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; padding: 2px 0; font-size:12px; color:#1A1F2E;"><strong style="color:#5C6370; font-weight:600;">${esc(f.day)}</strong></td><td class="body" align="right" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; padding: 2px 0; font-size:12px; color:#1A1F2E;">${esc(f.high)}/${esc(f.low)} ${esc(f.icon)}</td></tr>`).join('')}
                          </table>
                        </td>
                      </tr>
                    </table>
                    ${(d.sunrise || d.sunset) ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:14px 0 0 0; font-size:13px; line-height:1.5; color:#5C6370;">${d.sunrise ? `<strong style="color:#1A1F2E;">Sunrise:</strong> ${esc(d.sunrise)}` : ''}${d.sunrise && d.sunset ? ' <span style="color:#A0AEC0;">·</span> ' : ''}${d.sunset ? `<strong style="color:#1A1F2E;">Sunset:</strong> ${esc(d.sunset)}` : ''}</p>` : ''}
                    ${d.notes ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:14px 0 0 0; font-size:14px; line-height:1.55; color:#1A1F2E;">${rich(d.notes)}</p>` : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>`,

  story: (d) => `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="force-light" style="background-color:#FFFFFF; border-radius:8px; overflow:hidden;">
                ${d.imageUrl ? `<tr><td><img src="${esc(d.imageUrl)}" alt="${esc(d.imageAlt)}" width="600" class="img-fluid" style="display:block; width:100%; max-width:600px; height:auto;"></td></tr>` : ''}
                <tr>
                  <td class="px-mobile" style="padding: 26px 36px 8px 36px;">
                    ${d.eyebrow ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 10px 0; font-size:11px; font-weight:700; color:#153EA7; letter-spacing:1.4px; text-transform:uppercase;">${esc(d.eyebrow)}</p>` : ''}
                    <h1 class="display h1-mobile" style="font-family:'Fraunces',Georgia,serif; margin:0 0 14px 0; font-size:34px; line-height:1.15; font-weight:700; color:#1A1F2E; letter-spacing:-0.5px;">${esc(d.headline)}</h1>
                    ${d.caption ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 18px 0; font-size:12px; line-height:1.45; color:#5C6370; font-style:italic;">${rich(d.caption)}</p>` : ''}
                  </td>
                </tr>
                <tr>
                  <td class="px-mobile" style="padding: 0 36px 32px 36px;">
                    ${(() => {
                      const items = storyBodyItems(d as { body?: unknown; paragraphs?: unknown });
                      const paragraphIndices = items.map((it, i) => (it.type === 'paragraph' ? i : -1)).filter((i) => i >= 0);
                      const lastParagraphIdx = paragraphIndices[paragraphIndices.length - 1] ?? -1;
                      return items.map((item, i) => {
                        if (item.type === 'image') {
                          if (!item.url) return '';
                          const captionHtml = item.caption
                            ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:6px 0 0 0; font-size:12px; line-height:1.45; color:#5C6370; font-style:italic;">${rich(item.caption)}</p>`
                            : '';
                          return `<div style="margin:0 0 16px 0;"><img src="${esc(item.url)}" alt="${esc(item.alt)}" width="528" class="img-fluid" style="display:block; width:100%; max-width:528px; height:auto; border-radius:4px;">${captionHtml}</div>`;
                        }
                        const isLastParagraph = i === lastParagraphIdx;
                        const isLastItem = i === items.length - 1;
                        const margin = isLastItem ? '0' : '0 0 16px 0';
                        const leadHtml = item.lead ? `<strong style="color:#1A1F2E;">${esc(item.lead)}</strong> ` : '';
                        const inlineLink = isLastParagraph && d.readMoreUrl && d.readMoreLabel && d.readMoreStyle !== 'button'
                          ? ` <a href="${esc(d.readMoreUrl)}" class="brand-link" style="color:#153EA7; text-decoration:underline;">${esc(d.readMoreLabel)}</a>.`
                          : '';
                        return `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:${margin}; font-size:16px; line-height:1.65; color:#1A1F2E;">${leadHtml}${rich(item.body)}${inlineLink}</p>`;
                      }).join('');
                    })()}
                    ${d.readMoreStyle === 'button' && d.readMoreUrl && d.readMoreLabel ? `
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-top:18px;">
                      <tr>
                        <td style="background-color:#153EA7; border-radius:4px;">
                          <a href="${esc(d.readMoreUrl)}" class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; display:inline-block; padding:10px 22px; font-size:14px; font-weight:600; color:#FFFFFF; text-decoration:none; letter-spacing:0.3px;">${esc(d.readMoreLabel)} →</a>
                        </td>
                      </tr>
                    </table>` : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>`,

  sponsor: (d) => `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FFF8F2; border-radius:8px; border:1px solid #F4E0CC;">
                <tr>
                  <td class="px-mobile" style="padding: 24px 32px;">
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 8px 0; font-size:10px; font-weight:700; color:#A05A2C; letter-spacing:1.4px; text-transform:uppercase;">Together with ${esc(d.sponsorName)}</p>
                    <p class="display" style="font-family:'Fraunces',Georgia,serif; margin:0 0 10px 0; font-size:22px; font-weight:600; color:#1A1F2E; line-height:1.25;">${esc(d.headline)}</p>
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 16px 0; font-size:15px; line-height:1.55; color:#3A3F4A;">${rich(d.body)}</p>
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="background-color:#153EA7; border-radius:4px;">
                          <a href="${esc(d.ctaUrl)}" class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; display:inline-block; padding:10px 22px; font-size:14px; font-weight:600; color:#FFFFFF; text-decoration:none; letter-spacing:0.3px;">${esc(d.ctaLabel)} →</a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`,

  bulletin: (d) => `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="force-light" style="background-color:#FFFFFF; border-radius:8px;">
                <tr>
                  <td class="px-mobile" style="padding: 26px 36px 6px 36px;">
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 6px 0; font-size:11px; font-weight:700; color:#153EA7; letter-spacing:1.4px; text-transform:uppercase;">${esc(d.title)}</p>
                    ${d.subtitle ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 8px 0; font-size:14px; color:#5C6370; font-style:italic;">${rich(d.subtitle)}</p>` : ''}
                  </td>
                </tr>
                <tr>
                  <td class="px-mobile" style="padding: 0 36px 26px 36px;">
                    ${((d.items as BulletinItem[]) || []).map((item, i, arr) => {
                      const isLast = i === arr.length - 1;
                      const border = isLast ? '' : 'border-bottom:1px solid #EAEEF2;';
                      const padding = isLast ? '14px 0 0 0' : '14px 0';
                      const sourceLink = item.sourceUrl && item.sourceLabel ? ` <a href="${esc(item.sourceUrl)}" class="brand-link" style="color:#153EA7; text-decoration:underline;">${esc(item.sourceLabel)}</a>` : '';
                      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="${border}"><tr><td style="padding:${padding};"><p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0; font-size:15px; line-height:1.5; color:#1A1F2E;"><strong>${esc(item.headline)}:</strong> ${rich(item.body)}${sourceLink}</p></td></tr></table>`;
                    }).join('')}
                  </td>
                </tr>
              </table>
            </td>
          </tr>`,

  poll: (d) => {
    const prev = (d.previousResults as Array<{ label: string; percent: number }>) || [];
    const showPrev = !!(d.previousQuestion && prev.length > 0);
    return `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#E2F4FF; border-radius:8px;">
                <tr>
                  <td class="px-mobile" style="padding: 26px 32px;">
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 8px 0; font-size:11px; font-weight:700; color:#153EA7; letter-spacing:1.4px; text-transform:uppercase;">Reader Poll</p>
                    <p class="display" style="font-family:'Fraunces',Georgia,serif; margin:0 0 18px 0; font-size:22px; font-weight:600; color:#1A1F2E; line-height:1.25;">${esc(d.question)}</p>
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                      ${((d.options as PollOption[]) || []).map((opt, i, arr) => {
                        const padding = i === arr.length - 1 ? '0' : '0 0 10px 0';
                        return `<tr><td style="padding:${padding};"><a href="${esc(opt.url)}" class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; display:block; padding:13px 16px; background-color:#FFFFFF; border:1px solid #C9E2F2; border-radius:4px; color:#153EA7; font-size:15px; font-weight:600; text-decoration:none;">→ ${esc(opt.label)}</a></td></tr>`;
                      }).join('')}
                    </table>
                    ${showPrev ? `
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-top:1px dashed #C9E2F2; margin-top:22px;">
                      <tr><td style="height:22px; line-height:22px; font-size:0;">&nbsp;</td></tr>
                      <tr><td>
                        <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 6px 0; font-size:10px; font-weight:700; color:#5C6370; letter-spacing:1.4px; text-transform:uppercase;">${esc(d.previousLabel || "Yesterday's poll")}</p>
                        <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 14px 0; font-size:14px; color:#1A1F2E; font-style:italic;">${esc(d.previousQuestion)}</p>
                        ${prev.map((r) => {
                          const pct = Math.max(0, Math.min(100, Number(r.percent) || 0));
                          return `
                        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom:8px;">
                          <tr>
                            <td class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; font-size:12px; color:#1A1F2E; padding-bottom:3px;">${esc(r.label || '')}</td>
                            <td class="body" align="right" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; font-size:12px; color:#1A1F2E; font-weight:600; padding-bottom:3px;">${pct}%</td>
                          </tr>
                          <tr>
                            <td colspan="2" style="background-color:#FFFFFF; border-radius:3px; height:6px; line-height:6px; font-size:0;">
                              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="height:6px;"><tr>
                                <td width="${pct}%" style="background-color:#153EA7; height:6px; line-height:6px; font-size:0;">&nbsp;</td>
                                <td width="${100 - pct}%" style="height:6px; line-height:6px; font-size:0;">&nbsp;</td>
                              </tr></table>
                            </td>
                          </tr>
                        </table>`;
                        }).join('')}
                      </td></tr>
                    </table>
                    ` : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;
  },

  calendar: (d, ctx) => {
    interface CalEv extends EventLike {
      name?: string;
      description?: string;
      linkLabel?: string;
      linkUrl?: string;
      url?: string; // legacy
    }
    const events = resolveCalendarEvents(d, ctx.dbEvents ?? []) as CalEv[];
    const today = todayIso();
    return `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="force-light" style="background-color:#FFFFFF; border-radius:8px;">
                <tr>
                  <td class="px-mobile" style="padding: 26px 36px 6px 36px;">
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 6px 0; font-size:11px; font-weight:700; color:#153EA7; letter-spacing:1.4px; text-transform:uppercase;">${esc(d.title)}</p>
                    ${d.subtitle ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 8px 0; font-size:14px; color:#5C6370; font-style:italic;">${esc(d.subtitle)}</p>` : ''}
                  </td>
                </tr>
                <tr>
                  <td class="px-mobile" style="padding: 0 36px 26px 36px;">
                    ${events.length === 0 ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0; padding:14px 0; font-size:13px; color:#A0AEC0; font-style:italic;">No events yet.</p>` : events.map((ev, i, arr) => {
                      const isLast = i === arr.length - 1;
                      const border = isLast ? '' : 'border-bottom:1px solid #EAEEF2;';
                      const padding = isLast ? '14px 0 0 0' : '14px 0';
                      const when = formatEventDate(ev, today);
                      const url = ev.linkUrl || ev.url || '';
                      const label = ev.linkLabel || '';
                      const linkHtml = url && label
                        ? ` <a href="${esc(url)}" class="brand-link" style="color:#153EA7; text-decoration:underline;">${esc(label)} →</a>`
                        : '';
                      const descHtml = ev.description ? ` ${esc(ev.description)}` : '';
                      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="${border}">
                        <tr><td style="padding:${padding};">
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0; font-size:15px; line-height:1.5; color:#1A1F2E;"><strong>${esc(when)}:</strong> ${esc(ev.name || '')}${descHtml}${linkHtml}</p>
                        </td></tr>
                      </table>`;
                    }).join('')}
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;
  },

  signoff: (d) => `
          <tr>
            <td class="card-gap" style="padding-bottom: 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="force-light" style="background-color:#FFFFFF; border-radius:8px;">
                <tr>
                  <td class="px-mobile" style="padding: 28px 36px 24px 36px;">
                    <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 6px 0; font-size:15px; line-height:1.5; color:#1A1F2E;">${rich(d.closing)}</p>
                    <p class="display" style="font-family:'Fraunces',Georgia,serif; margin:0 0 22px 0; font-size:18px; font-weight:600; color:#153EA7; font-style:italic;">${esc(d.signature)}</p>
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-top:1px dashed #C9E2F2;">
                      <tr>
                        <td align="center" style="padding: 22px 0 4px 0;">
                          <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 12px 0; font-size:14px; line-height:1.5; color:#5C6370;">${esc(d.forwardPrompt)}</p>
                          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                              <td style="background-color:#FFFFFF; border:1.5px solid #153EA7; border-radius:4px;">
                                <a href="${esc(d.forwardUrl)}" class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; display:inline-block; padding:9px 20px; font-size:14px; font-weight:600; color:#153EA7; text-decoration:none;">${esc(d.forwardLabel)}</a>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`,

  footer: (d) => {
    const phoneHref = d.editorPhone ? String(d.editorPhone).replace(/[^\d+]/g, '') : '';
    return `
          <tr>
            <td align="center" class="px-mobile" style="padding: 12px 36px 24px 36px;">
              ${(d.editorEmail || d.editorPhone) ? `<p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 14px 0; font-size:12px; line-height:1.55; color:#5C6370;"><strong style="color:#1A1F2E;">Reach the editor:</strong> ${d.editorEmail ? `<a href="mailto:${esc(d.editorEmail)}" style="color:#153EA7; text-decoration:underline;">${esc(d.editorEmail)}</a>` : ''}${d.editorEmail && d.editorPhone ? ' <span style="color:#A0AEC0;">·</span> ' : ''}${d.editorPhone ? `<a href="tel:${esc(phoneHref)}" style="color:#153EA7; text-decoration:underline;">${esc(d.editorPhone)}</a>` : ''}</p>` : ''}
              <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  ${((d.socials as Social[]) || []).map((s, i) => `${i > 0 ? '<td style="color:#A0AEC0; padding: 0 4px; font-family:Helvetica,Arial,sans-serif; font-size:13px;">·</td>' : ''}<td style="padding: 0 8px;"><a href="${esc(s.url)}" style="color:#153EA7; font-family:'Source Sans 3',Helvetica,Arial,sans-serif; font-size:13px; font-weight:600; text-decoration:none;">${esc(s.label)}</a></td>`).join('')}
                </tr>
              </table>
              <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:18px 0 6px 0; font-size:12px; line-height:1.55; color:#5C6370;">${rich(d.tagline)}</p>
              <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 14px 0; font-size:12px; line-height:1.55; color:#8A929E;">${esc(d.address)}</p>
              <p class="body" style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0; font-size:12px; line-height:1.55; color:#8A929E;">
                <a href="{{unsubscribe_url}}" style="color:#5C6370; text-decoration:underline;">Unsubscribe</a>
                &nbsp;·&nbsp;
                <a href="https://penobscotmorning.com" style="color:#5C6370; text-decoration:underline;">Web version</a>
              </p>
            </td>
          </tr>`;
  },
};

export function renderBlockHtml(block: Block, ctx: RenderContext = {}): string {
  const renderer = HTML_RENDERERS[block.type];
  return renderer ? renderer(block.data as Record<string, unknown>, ctx) : '';
}

export function renderFullHtml(
  draft: Pick<Draft, 'blocks' | 'preheader'>,
  ctx: RenderContext = {},
): string {
  const blocksHtml = draft.blocks.map((b) => renderBlockHtml(b, ctx)).join('\n');
  const subjectHidden = draft.preheader ? esc(draft.preheader) : '';
  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <title>Penobscot Morning</title>
  <!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700;9..144,800&family=Source+Sans+3:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    body, table, td, a { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { -ms-interpolation-mode: bicubic; border: 0; outline: none; text-decoration: none; display: block; }
    table { border-collapse: collapse !important; }
    body { height: 100% !important; margin: 0 !important; padding: 0 !important; width: 100% !important; }
    a { text-decoration: none; }
    a.brand-link { color: #153EA7; text-decoration: underline; text-underline-offset: 2px; }
    @media screen and (max-width: 620px) {
      .container { width: 100% !important; max-width: 100% !important; }
      .px-mobile { padding-left: 20px !important; padding-right: 20px !important; }
      .py-mobile { padding-top: 24px !important; padding-bottom: 24px !important; }
      .weather-stack { display: block !important; width: 100% !important; max-width: 100% !important; padding: 16px 18px !important; border-right: 0 !important; border-bottom: 1px solid #D6E7F4 !important; }
      .weather-stack-last { display: block !important; width: 100% !important; max-width: 100% !important; padding: 16px 18px !important; border-right: 0 !important; }
      .h1-mobile { font-size: 30px !important; line-height: 1.15 !important; }
      .img-fluid { width: 100% !important; height: auto !important; max-width: 100% !important; }
      .logo-mobile { width: 200px !important; height: auto !important; }
      .card-gap { padding-bottom: 14px !important; }
    }
    @media (prefers-color-scheme: dark) {
      .force-light { background-color: #FFFFFF !important; color: #1A1F2E !important; }
    }
  </style>
</head>
<body style="margin:0; padding:0; background-color:#EFF5FA; font-family: 'Source Sans 3', 'Helvetica Neue', Helvetica, Arial, sans-serif;">
  <div style="display:none; font-size:1px; color:#EFF5FA; line-height:1px; max-height:0px; max-width:0px; opacity:0; overflow:hidden;">${subjectHidden}&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;</div>
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#EFF5FA;">
    <tr>
      <td align="center" style="padding: 24px 12px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" class="container" style="width:600px; max-width:600px;">
${blocksHtml}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// Pure-text fallback rendering. Plain text comes from titles, bodies,
// and event names — strips all HTML and inline marks. Keeps {{merge}}
// tokens intact so personalize.ts can substitute them per recipient.
export function renderFullText(draft: Pick<Draft, 'blocks' | 'preheader'>): string {
  const lines: string[] = [];
  if (draft.preheader) lines.push(draft.preheader, '');
  for (const block of draft.blocks) {
    const text = renderBlockText(block);
    if (text.trim()) {
      lines.push(text.trim());
      lines.push('');
    }
  }
  return lines.join('\n').trimEnd() + '\n';
}

function stripMd(s: unknown): string {
  // Reduce inline markdown to plain text. Same surface as inlineMdToHtml.
  let out = String(s ?? '');
  out = out.replace(/\[([^\]\n]+?)\]\(([^)\s]+)\)/g, '$1 ($2)');
  out = out.replace(/\*\*([^*\n]+?)\*\*/g, '$1');
  out = out.replace(/__([^_\n]+?)__/g, '$1');
  out = out.replace(/(?<![*\w])\*([^*\n]+?)\*(?![*\w])/g, '$1');
  out = out.replace(/(?<![_\w])_([^_\n]+?)_(?![_\w])/g, '$1');
  return out;
}

function renderBlockText(block: Block): string {
  const d = block.data as Record<string, unknown>;
  switch (block.type) {
    case 'header':
      return `${d.dateLine ?? ''} — ${d.editionMode === 'custom' ? d.editionNumber : `Edition Nº ${d.editionNumber ?? ''}`}`;
    case 'greeting':
      return `${d.lead ?? ''} ${stripMd(d.body)}\n— ${d.signature ?? ''}`;
    case 'weather': {
      const f = (d.forecast as Array<{ day: string; high: string; low: string }>) ?? [];
      return [
        'WEATHER',
        `Today: ${d.todayTemp ?? ''} ${d.todayLabel ?? ''} (H ${d.todayHigh ?? ''} / L ${d.todayLow ?? ''})`,
        `Tomorrow: ${d.tomorrowTemp ?? ''} ${d.tomorrowLabel ?? ''} (H ${d.tomorrowHigh ?? ''} / L ${d.tomorrowLow ?? ''})`,
        ...f.map((day) => `${day.day}: ${day.high}/${day.low}`),
        d.sunrise || d.sunset ? `Sunrise ${d.sunrise ?? ''} · Sunset ${d.sunset ?? ''}` : '',
        d.notes ? stripMd(d.notes) : '',
      ].filter(Boolean).join('\n');
    }
    case 'story': {
      const items = storyBodyItems(d as { body?: unknown; paragraphs?: unknown });
      const paragraphs = items
        .filter((it) => it.type === 'paragraph')
        .map((it) => {
          const p = it as { lead?: string; body?: string };
          return `${p.lead ? p.lead + ' ' : ''}${stripMd(p.body)}`;
        });
      const readMore = d.readMoreUrl && d.readMoreLabel ? `\n${d.readMoreLabel}: ${d.readMoreUrl}` : '';
      return `${d.eyebrow ? `[${d.eyebrow}] ` : ''}${d.headline ?? ''}\n\n${paragraphs.join('\n\n')}${readMore}`;
    }
    case 'sponsor':
      return `Together with ${d.sponsorName ?? ''}\n${d.headline ?? ''}\n${stripMd(d.body)}\n${d.ctaLabel ?? ''}: ${d.ctaUrl ?? ''}`;
    case 'bulletin': {
      const items = (d.items as Array<{ headline: string; body: string; sourceLabel?: string; sourceUrl?: string }>) ?? [];
      return `${d.title ?? ''}\n${d.subtitle ? stripMd(d.subtitle) + '\n' : ''}${items.map((it) => `- ${it.headline}: ${stripMd(it.body)}${it.sourceUrl ? ` (${it.sourceLabel || it.sourceUrl})` : ''}`).join('\n')}`;
    }
    case 'calendar': {
      const events = resolveCalendarEvents(d, []) as Array<{
        name?: string;
        startDate?: string;
        startTime?: string;
        endDate?: string;
        endTime?: string;
        description?: string;
        linkLabel?: string;
        linkUrl?: string;
      }>;
      const today = todayIso();
      return `${d.title ?? ''}\n${events.map((ev) => `- ${formatEventDate({ startDate: ev.startDate || '', startTime: ev.startTime, endDate: ev.endDate, endTime: ev.endTime }, today)}: ${ev.name ?? ''}${ev.description ? ' — ' + ev.description : ''}${ev.linkUrl ? ' (' + ev.linkUrl + ')' : ''}`).join('\n')}`;
    }
    case 'poll': {
      const opts = (d.options as Array<{ label: string; url: string }>) ?? [];
      return `${d.question ?? ''}\n${opts.map((o) => `- ${o.label}: ${o.url}`).join('\n')}`;
    }
    case 'signoff':
      return `${stripMd(d.closing)}\n${d.signature ?? ''}\n${d.forwardPrompt ?? ''} ${d.forwardUrl ?? ''}`;
    case 'footer':
      return `${stripMd(d.tagline)}\n${d.address ?? ''}\nUnsubscribe: {{unsubscribe_url}}`;
    default:
      return '';
  }
}
